import type { ExpandStatus } from "@stryker/core/types/expandable";
import type { PropsWithChildren, ReactElement, ReactNode } from "react";
import ChevronIcon from "@/assets/icons/chevron.svg?react";
import FlagIcon from "@/assets/icons/flag.svg?react";
import LockUnlocked from "@/assets/icons/lock-unlocked.svg?react";
import VerifiedIcon from "@/assets/icons/verified.svg?react";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

type ExpandableSectionProps = {
  titleClassName?: string;
  className?: string;
  title: ReactNode;
  status: ExpandStatus;
  isExpanded?: boolean;
  setIsExpanded?: () => void;
};

export default function ExpandableSection({
  children,
  title,
  setIsExpanded,
  status,
  titleClassName,
  className,
  isExpanded,
}: PropsWithChildren<ExpandableSectionProps>) {
  if (status === "hidden") {
    return null;
  }

  const expandHandler = () => {
    if (status === "blocked") return;
    if (setIsExpanded) {
      setIsExpanded();
    }
  };

  const shouldBeOpen = isExpanded ?? status === "open";

  let Icon: ReactElement | null = <LockUnlocked />;
  if (status === "open") {
    Icon = <FlagIcon />;
  } else if (status === "done") {
    Icon = <VerifiedIcon />;
  } else if (status === "canBeOpened") {
    Icon = null;
  }

  let borderColor = "border-gray-200 ";
  if (status === "open" || (status === "canBeOpened" && title !== "Notes")) {
    borderColor = "border-important-700";
  } else if (status === "done") {
    borderColor = "border-success-800";
  }

  return (
    <div className={cn("schedule rounded-md border border-solid px-8 py-6", borderColor, className)}>
      <div
        className={cn(
          "flex items-center gap-2",
          shouldBeOpen && "mb-7",
          status === "blocked" && "text-gray-600",
          titleClassName
        )}
      >
        <button onClick={expandHandler}>
          <ChevronIcon
            className={cn(
              shouldBeOpen ? "rotate-90" : "-rotate-90",
              status === "blocked" ? "fill-[#A5A5A5]" : "cursor-pointer"
            )}
          />
        </button>
        <Typography
          variant="text-xl"
          className={cn(status === "blocked" ? "text-gray-400" : "text-gray-800")}
          weight="semibold"
        >
          {title}
        </Typography>

        <div className="ml-auto">{Icon}</div>
      </div>
      {shouldBeOpen && children}
    </div>
  );
}
